import React from 'react';
import { Check } from 'lucide-react';
import { colors, typography, spacing, transitions } from '../styles/shared';

const styles = {
  stepper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: spacing[8],
    position: 'relative',
    padding: `${spacing[2]} ${spacing[4]}`,
  },
  stepsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    width: '100%',
    position: 'relative',
    zIndex: 1,
    gap: spacing[4],
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[3],
    flex: '1 1 0',
    position: 'relative',
  },
  stepButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[3],
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    transition: transitions.bounce,
    width: '100%',
    '&:disabled': {
      cursor: 'default',
      opacity: 0.7,
    },
    '&:not(:disabled):hover': {
      transform: 'translateY(-2px)',
    },
  },
  stepCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: colors.gray[100],
    border: `3px solid ${colors.gray[300]}`,
    color: colors.gray[600],
    ...typography.h4,
    transition: transitions.bounce,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-4px',
      left: '-4px',
      right: '-4px',
      bottom: '-4px',
      borderRadius: '50%',
      border: `2px solid transparent`,
      transition: transitions.smooth,
    },
  },
  stepCircleActive: {
    backgroundColor: colors.primary[50],
    borderColor: colors.primary[500],
    color: colors.primary[600],
    '&::after': {
      borderColor: colors.primary[200],
    },
  },
  stepCircleCompleted: {
    backgroundColor: colors.primary[500],
    borderColor: colors.primary[600],
    color: colors.background.page,
    '&::after': {
      borderColor: colors.primary[300],
    },
  },
  stepLabel: {
    ...typography.button,
    color: colors.gray[600],
    transition: transitions.smooth,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  stepLabelActive: {
    color: colors.primary[600],
    fontWeight: '600',
  },
  stepLabelCompleted: {
    color: colors.gray[900],
  },
  stepDescription: {
    ...typography.caption,
    color: colors.gray[500],
    textAlign: 'center',
    maxWidth: '120px',
    transition: transitions.smooth,
  },
  stepDescriptionActive: {
    color: colors.primary[500],
  },
  stepDescriptionCompleted: {
    color: colors.gray[700],
  },
};

const Stepper = ({ steps, currentStep, setCurrentStep, isStepComplete }) => {
  const handleStepClick = (index) => {
    if (index < currentStep || isStepComplete(index - 1)) {
      setCurrentStep(index);
    }
  };

  return (
    <div style={styles.stepper}>
      <div style={styles.stepsContainer}>
        {steps.map((step, index) => {
          const isActive = currentStep === index;
          const isCompleted = isStepComplete(index);
          const isClickable = index < currentStep || isStepComplete(index - 1);

          return (
            <div key={index} style={styles.step}>
              <button
                style={styles.stepButton}
                onClick={() => handleStepClick(index)}
                disabled={!isClickable}
                title={isClickable ? 'Go to this step' : 'Complete previous steps first'}
              >
                <div
                  style={{
                    ...styles.stepCircle,
                    ...(isActive && styles.stepCircleActive),
                    ...(isCompleted && styles.stepCircleCompleted),
                  }}
                >
                  {isCompleted ? (
                    <Check size={24} strokeWidth={3} />
                  ) : (
                    index + 1
                  )}
                </div>
                <div>
                  <span
                    style={{
                      ...styles.stepLabel,
                      ...(isActive && styles.stepLabelActive),
                      ...(isCompleted && styles.stepLabelCompleted),
                    }}
                  >
                    {step}
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stepper; 